
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import { deserialise, serialise } from 'kitsu-core'

import { getRequest } from '@/plugins/api-axios'

import MixinInterface from '@/_mixins/index'
import ObservationMixin from '@/_mixins/observation'
import { getURL } from '@/utils/axios'
import { observationIcons } from '@/types/observation'

import SeveritySelect from '@/views/Project/Observation/components/severity.vue'
import DateTimeDropdown from '@/views/Project/Observation/components/observation/datetime.vue'
import ContractorPicker from '@/views/Project/Observation/components/contractorPicker.vue'
import TreeViewPicker from '@/views/Project/Observation/components/observation/treeViewPicker.vue'
import LocationDrawing from '@/views/Project/Observation/components/locationDrawing.vue'
import LocationDrawingChip from '@/views/Project/Observation/components/locationDrawingChip.vue'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import DropZone from '@/components/Picker/DropZone/DropZone.vue'
import Attachment from '@/components/Attachment/Attachment.vue'
import SelectedAttachment from '@/views/Project/Observation/components/selectedAttachment.vue'
import { isWritable } from '@/utils/app'

@Component({
    mixins: [ObservationMixin],
    components: {
        SeveritySelect,
        DateTimeDropdown,
        ContractorPicker,
        TreeViewPicker,
        LocationDrawing,
        LocationDrawingChip,
        RichTextField,
        SelectedAttachment,
        DropZone,
        Attachment,
    },
})
export default class AnonumousProjectObservation extends Mixins<MixinInterface>(ObservationMixin) {
    tools = [
        'header',
        'linkTool',
        'list',
        'embed',
        'quote',
        'table',
        'delimiter',
        'Marker',
        'underline',
    ]

    observationIcons = observationIcons
    url_observation = process.env.VUE_APP_OBSERVATIONS_BASE_URL

    //CONSTRAINTS
    constraints(key: string, constraint: string) {
        if (!this.observation) return false
        let res = true
        if (
            key in this.observation.constraints &&
            constraint in this.observation.constraints[key]
        ) {
            res = this.observation.constraints[key][constraint]
        }
        return res
    }

    isWritable(key: string) {
        return this.constraints(key, 'writable')
    }

    isAuthorized(key: string) {
        return this.constraints(key, 'authorized')
    }

    isRelevant(key: string) {
        return this.constraints(key, 'relevant')
    }

    get projectId() {
        return this.$route.params.id
    }

    projectObservation = null as null | Record<string, any>
    observationTemplate = null as null | Record<string, any>
    observation = null as null | Record<string, any>
    attachments = [] as Array<any>
    selectedAttachment = null as null | Record<string, any>
    isLoading = true
    isValid = false
    editorId = crypto['randomUUID']()
    get canFormBeUpdated() {
        return !this.isLoadingSubmit && this.observation?.id == 0
    }

    get categories() {
        return this.projectId
            ? this.$store.getters.observationCategories.filter((item: any) => {
                  return item.project.data?.id == this.projectId
              })
            : this.$store.getters.observationCategories
    }

    get causes() {
        return this.projectId
            ? this.$store.getters.observationCauses.filter((item: any) => {
                  return item.project.data?.id == this.projectId
              })
            : this.$store.getters.observationCauses
    }

    get locations() {
        return this.projectId
            ? this.$store.getters.projectLocations.filter((item: any) => {
                  return item.project.data?.id == this.projectId
              })
            : this.$store.getters.projectLocations
    }

    async createObservation() {
        if (!this.observation) return
        const payload = serialise('observation', {
            ...(this.observation.severity && { severity: this.observation.severity }),
            ...(this.observation.happenedAt && { happenedAt: this.observation.happenedAt }),
            description: this.observation.description,
            ...(this.observation.actionTaken && { actionTaken: this.observation.actionTaken }),
            ...(this.observation.locationDrawingX && {
                locationDrawingX: this.observation.locationDrawingX,
            }),
            ...(this.observation.locationDrawingY && {
                locationDrawingY: this.observation.locationDrawingY,
            }),
            ...(this.observation.locationDrawing?.data && {
                locationDrawing: this.observation.locationDrawing,
            }),
            contractors: this.observation.contractors,
            ...(this.observation.category?.data && { category: this.observation.category }),
            ...(this.observation.cause?.data && { cause: this.observation.cause }),
            ...(this.observation.location?.data && { location: this.observation.location }),

            project: this.observation.project,
        })

        this.observation = await this.postObservation(payload, true)
    }

    // Attachments
    loadingAttachment = false
    get requirments() {
        return this.$store.getters.attachmentConstraints.maxBytes
            ? this.$store.getters.attachmentConstraints
            : this.getFileRequirements()
    }

    @Watch('selectedAttachment', { deep: true })
    handleActiveAttachmentUpdate() {
        if (!this.selectedAttachment) return

        let index = this.attachments.findIndex((attachment: any) => {
            return attachment.fileId == this.selectedAttachment?.fileId
        })
        this.attachments.splice(index, 1, this.selectedAttachment)
    }

    handleDeletedAttachment(selectedAttachment: Record<string, any>) {
        let index = this.attachments.findIndex((attachment: any) => {
            return attachment.fileId == selectedAttachment.fileId
        })
        this.attachments.splice(index, 1)
    }

    setMainAttachment(selectedAttachment: Record<string, any>) {
        if (!this.observation || !isWritable(this.observation, 'mainAttachment')) return
        this.observation.mainAttachment = {
            data: { type: 'file', id: selectedAttachment.fileId },
        }
    }

    passFiles(file: any) {
        this.attachments.push({ ...file.attributes, hidden: false })
    }

    async postAttachments() {
        for (let index = 0; index < this.attachments.length; index++) {
            if (!this.observation) return

            let data = serialise('attachment', {
                description: this.attachments[index].description,
                fileId: this.attachments[index].fileId,
                hidden: this.attachments[index].hidden,
                observation: {
                    data: {
                        id: this.observation.id,
                        type: 'observation',
                    },
                },
            })

            console.log('postAttachments', data)

            const attachment = await this.postAttachmentObservation(data, true)

            this.observation.attachments.data.push(attachment)
        }
    }

    isLoadingSubmit = false
    successSnackbar = false

    async submit() {
        if (!this.observation) return
        try {
            this.isLoadingSubmit = true
            await this.createObservation()
            await this.postAttachments()
            // publish to publishAnonymous endpoint if settings allows for it
            this.observation = await this.publishPrivateObservation(
                this.observation.id,
                this.projectObservation?.allowAnonymousObservations,
                true
            )
            this.successSnackbar = true
        } finally {
            this.isLoadingSubmit = false
        }
    }

    async getObservationProject() {
        const { data } = await getRequest(
            getURL({
                base: this.url_observation,
                path: `projects/${this.projectId}`,
                params: {
                    include: 'settings',
                },
            }),
            {
                Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt',
            }
        )

        this.projectObservation = deserialise(data).data.settings.data
    }

    // Load observation template and initializing observation object
    async initialiseObservation() {
        this.observationTemplate = await this.getObservationTemplateByProject(this.projectId, true)
        this.observation = cloneDeep(this.observationTemplate)
        if (this.observation) {
            this.observation.happenedAt = new Date()
            this.observation.description = null
            this.editorId = crypto['randomUUID']()
            this.attachments = []
            this.selectedAttachment = null
        }
    }

    async mounted() {
        try {
            this.isLoading = true
            await this.getObservationProject()
            await this.initialiseObservation()
        } finally {
            this.isLoading = false
        }
    }
}
